import {combine, createEvent, merge, sample} from 'effector';
import {createGate} from 'effector-react';

import {showRestError} from '@app/errors';
import {rootDomain} from '@app/model';
import {InnerSortingsElement} from '@shared/types/grid';

import * as api from './api';
import {collectViolationTypeCodes, createViolationTypeTree} from './lib';
import {
    FilterNode,
    SearchResponse,
    ViolationThresholdDto,
    ViolationThresholdPayload,
    ViolationTypeDto,
    ViolationTypePayload,
} from './types';

const domain = rootDomain.createDomain('Catalogs');

export const ViolationThresholdsGate = createGate('ViolationThresholds');
export const ViolationTypesGate = createGate('ViolationTypes');

const fetchViolationThresholdsFx = domain.createEffect({
    handler: api.fetchViolationThresholds,
});
const createViolationThresholdFx = domain.createEffect({
    handler: api.createViolationThreshold,
});
const updateViolationThresholdFx = domain.createEffect({
    handler: api.updateViolationThreshold,
});
const fetchViolationTypesFx = domain.createEffect({handler: api.fetchViolationTypes});
const updateViolationTypeFx = domain.createEffect({handler: api.updateViolationType});

[
    fetchViolationThresholdsFx,
    createViolationThresholdFx,
    updateViolationThresholdFx,
    fetchViolationTypesFx,
    updateViolationTypeFx,
].forEach((fx) => fx.failData.watch(showRestError));

const thresholds$ = domain
    .createStore<SearchResponse<ViolationThresholdDto> | null>(null)
    .on(fetchViolationThresholdsFx.doneData, (_, data) => data)
    .reset(ViolationThresholdsGate.close);

const violationTypes$ = domain
    .createStore<ViolationTypeDto[]>([])
    .on(fetchViolationTypesFx.doneData, (_, data) => data.data ?? [])
    .reset(ViolationThresholdsGate.close, ViolationTypesGate.close);

const thresholdViolationTypeCodes$ = domain
    .createStore<string[]>([])
    .reset(ViolationThresholdsGate.close);
const thresholdTaxPeriod$ = domain
    .createStore<Date | null>(null)
    .reset(ViolationThresholdsGate.close);
const thresholdSortings$ = domain
    .createStore<Record<string, boolean | undefined>>({taxPeriod: true})
    .reset(ViolationThresholdsGate.close);
const thresholdPageNumber$ = domain.createStore(0).reset(ViolationThresholdsGate.close);
const thresholdPageSize$ = domain.createStore(10).reset(ViolationThresholdsGate.close);

export const thresholdViolationTypeCodesChanged = createEvent<string[]>();
export const thresholdTaxPeriodChanged = createEvent<Date | null>();
export const thresholdSortingsChanged = createEvent<InnerSortingsElement[]>();
export const thresholdPageNumberChanged = createEvent<number>();
export const thresholdPageSizeChanged = createEvent<number>();
export const saveViolationThreshold = createEvent<{
    id?: string;
    payload: ViolationThresholdPayload;
}>();
export const saveViolationType = createEvent<{id: string; payload: ViolationTypePayload}>();

thresholdViolationTypeCodes$.on(thresholdViolationTypeCodesChanged, (_, value) => value);
thresholdTaxPeriod$.on(thresholdTaxPeriodChanged, (_, value) => value);
thresholdSortings$.on(thresholdSortingsChanged, (state, sortings) => ({
    ...state,
    ...Object.fromEntries(sortings.map(({id, value}) => [id, value])),
}));
thresholdPageNumber$.on(thresholdPageNumberChanged, (_, value) => value);
thresholdPageSize$.on(thresholdPageSizeChanged, (_, value) => value);
thresholdPageNumber$.reset(
    thresholdViolationTypeCodesChanged,
    thresholdTaxPeriodChanged,
    thresholdPageSizeChanged,
);

sample({
    clock: saveViolationThreshold,
    filter: ({id}) => !id || id === 'new',
    fn: ({payload}) => payload,
    target: createViolationThresholdFx,
});

sample({
    clock: saveViolationThreshold,
    filter: ({id}) => Boolean(id) && id !== 'new',
    fn: ({id, payload}) => ({id: id as string, payload}),
    target: updateViolationThresholdFx,
});

sample({
    clock: saveViolationType,
    target: updateViolationTypeFx,
});

sample({
    clock: [ViolationThresholdsGate.open, ViolationTypesGate.open],
    target: fetchViolationTypesFx,
});

const buildThresholdFilter = ({
    selectedCodes,
    taxPeriod,
    violationTypes,
}: {
    selectedCodes: string[];
    taxPeriod: Date | null;
    violationTypes: ViolationTypeDto[];
}): FilterNode | null => {
    const children: FilterNode[] = [];

    if (selectedCodes.length) {
        const selectedIds = violationTypes
            .filter((item) =>
                collectViolationTypeCodes(
                    selectedCodes,
                    createViolationTypeTree(violationTypes),
                ).includes(item.code),
            )
            .map((item) => item.id);

        if (selectedIds.length) {
            children.push({type: 'IN', field: 'violationType', values: selectedIds});
        }
    }

    if (taxPeriod) {
        children.push({
            type: 'EQ',
            field: 'taxPeriodYear',
            values: [String(taxPeriod.getFullYear())],
        });
        children.push({
            type: 'EQ',
            field: 'taxPeriodMonth',
            values: [String(taxPeriod.getMonth() + 1)],
        });
    }

    if (!children.length) return null;
    return children.length === 1 ? children[0] : {type: 'AND', children};
};

const mapThresholdSorts = (sortings: Record<string, boolean | undefined>) => {
    const [field, value] = Object.entries(sortings).find(([, sort]) => sort != null) ?? [];
    if (!field || value == null) return null;
    const order: 'DESC' | 'ASC' = value ? 'DESC' : 'ASC';

    if (field === 'taxPeriod') {
        return [
            {field: 'taxPeriodYear', order},
            {field: 'taxPeriodMonth', order},
        ];
    }

    return [{field, order} as const];
};

sample({
    clock: merge([
        ViolationThresholdsGate.open,
        fetchViolationTypesFx.done,
        createViolationThresholdFx.done,
        updateViolationThresholdFx.done,
        thresholdViolationTypeCodesChanged,
        thresholdTaxPeriodChanged,
        thresholdSortingsChanged,
        thresholdPageNumberChanged,
        thresholdPageSizeChanged,
    ]),
    source: combine({
        selectedCodes: thresholdViolationTypeCodes$,
        taxPeriod: thresholdTaxPeriod$,
        sortings: thresholdSortings$,
        page: thresholdPageNumber$,
        size: thresholdPageSize$,
        violationTypes: violationTypes$,
    }),
    filter: ViolationThresholdsGate.status,
    fn: ({selectedCodes, taxPeriod, sortings, page, size, violationTypes}) => ({
        filter: buildThresholdFilter({selectedCodes, taxPeriod, violationTypes}),
        sorts: mapThresholdSorts(sortings),
        page: {page, size},
    }),
    target: fetchViolationThresholdsFx,
});

sample({
    clock: updateViolationTypeFx.done,
    filter: ViolationTypesGate.status,
    target: fetchViolationTypesFx,
});

const thresholdPending$ = combine(
    [
        fetchViolationThresholdsFx.pending,
        createViolationThresholdFx.pending,
        updateViolationThresholdFx.pending,
        fetchViolationTypesFx.pending,
    ],
    (list) => list.some(Boolean),
);

const violationTypesPending$ = combine(
    [fetchViolationTypesFx.pending, updateViolationTypeFx.pending],
    (list) => list.some(Boolean),
);

const thresholdFilters$ = combine({
    violationTypeCodes: thresholdViolationTypeCodes$,
    taxPeriod: thresholdTaxPeriod$,
});

export const violationThresholdsModel = {
    state: combine({
        data: thresholds$,
        violationTypes: violationTypes$,
        filters: thresholdFilters$,
        sortings: thresholdSortings$,
        pageNumber: thresholdPageNumber$,
        pageSize: thresholdPageSize$,
        pending: thresholdPending$,
    }),
    violationTypeCodesChanged: thresholdViolationTypeCodesChanged,
    taxPeriodChanged: thresholdTaxPeriodChanged,
    sortingsChanged: thresholdSortingsChanged,
    pageNumberChanged: thresholdPageNumberChanged,
    pageSizeChanged: thresholdPageSizeChanged,
    save: saveViolationThreshold,
};

export const violationTypesModel = {
    state: combine({
        data: violationTypes$,
        pending: violationTypesPending$,
    }),
    save: saveViolationType,
};
